import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, Robot, CheckCircle, UserCircle, Lifebuoy, Headset, Sparkle } from "@phosphor-icons/react";
import AvaBuildChat from "@/components/AvaBuildChat";

export default function Support() {
  const [meta, setMeta] = useState({ categories: [] });
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "Other", product: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // live chat
  const [product, setProduct] = useState("");
  const [visitor, setVisitor] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [assignee, setAssignee] = useState("ai");
  const [agentName, setAgentName] = useState("");
  const [msg, setMsg] = useState("");
  const [thinking, setThinking] = useState(false);
  const [buildMode, setBuildMode] = useState(false);
  const endRef = useRef(null);
  const sessionRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.flow === "activation") {
      setMessages([{
        role: "ai",
        author: "Activation Walkthrough",
        text:
          "👋 Welcome to the AI iD Card activation walkthrough!\n\n" +
          "To get you activated, please have these three items ready:\n" +
          "1) Your Member Number\n" +
          "2) Your Key\n" +
          "3) Your registered email address\n\n" +
          "Don't have an account yet? Please register first, then come back with those three items.\n\n" +
          "Whenever you're ready, type below and we'll get started.",
      }]);
    }
  }, [location.state]);

  useEffect(() => {
    api.get("/support/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/support/products").then((r) => {
      const list = r.data.products || [];
      setProducts(list);
      if (list.length) setProduct(list[0].product);
    }).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, thinking]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      setResult(data);
      toast.success(data.needs_human ? "Ticket submitted — a human will follow up" : "Answered by AI Support");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  const mapThread = (th) => th.map((m) => ({ role: m.role, text: m.text, author: m.author }));

  const refresh = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const { data } = await api.get(`/support/chat/${sid}/messages?after=0`);
      setMessages(mapThread(data.messages || []));
      setAssignee(data.assignee);
      if (data.agent_name) setAgentName(data.agent_name);
    } catch { /* keep last state */ }
  }, []);

  // poll for human/agent replies while a session is open
  useEffect(() => {
    if (!sessionId) return;
    sessionRef.current = sessionId;
    const id = setInterval(() => refresh(sessionId), 4000);
    return () => clearInterval(id);
  }, [sessionId, refresh]);

  const sendChat = async () => {
    if (!msg.trim() || thinking) return;
    const text = msg.trim();
    setMessages((c) => [...c, { role: "customer", text }]);
    setMsg("");
    setThinking(true);
    try {
      const { data } = await api.post("/support/chat", { product, message: text, name: visitor || "Visitor", session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setAssignee(data.assignee);
      if (data.agent_name) setAgentName(data.agent_name);
      await refresh(data.session_id || sessionId);
    } catch (err) {
      setMessages((c) => [...c, { role: "system", text: "Sorry, I hit an error. Please submit a ticket." }]);
    } finally { setThinking(false); }
  };

  const isHuman = assignee === "human";

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 pt-28 pb-20">
        <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-3">// Support</p>
        <h1 className="font-mono-p font-bold uppercase tracking-tight text-3xl sm:text-4xl">How can we help?</h1>
        <p className="mt-3 text-sm text-zinc-500 max-w-xl flex items-center gap-2">
          <Lifebuoy size={16} className="text-cyan-400" /> A hybrid team — AI specialists handle quick questions instantly; humans take over the rest.
        </p>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {/* Contact form */}
          <div className="border border-zinc-800 bg-[#090A0C] p-7">
            <h2 className="font-mono-p font-bold uppercase tracking-tight text-lg mb-5">Submit a Ticket</h2>
            {result ? (
              <div data-testid="ticket-result" className="space-y-4">
                <div className={`flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest ${result.needs_human ? "text-amber-400" : "text-green-400"}`}>
                  {result.needs_human ? "Escalated to a human" : <><CheckCircle size={16} /> Resolved by AI</>}
                </div>
                <div className="border border-zinc-800 bg-black p-4">
                  <div className="flex items-center gap-2 text-cyan-400 text-xs font-mono-p uppercase mb-2"><Robot size={15} /> AI Support</div>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{result.ai_reply}</p>
                </div>
                <button onClick={() => { setResult(null); setForm({ name: "", email: "", subject: "", category: "Other", product: "", message: "" }); }}
                  data-testid="new-ticket-btn" className="font-mono-p text-xs uppercase tracking-widest border border-zinc-700 px-4 py-2.5 text-zinc-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors">
                  Submit another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="support-name" required placeholder="Name" value={form.name} onChange={set("name")} className="bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" />
                  <input data-testid="support-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" />
                </div>
                <input data-testid="support-subject" required placeholder="Subject" value={form.subject} onChange={set("subject")} className="w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" />
                <div className="grid grid-cols-2 gap-3">
                  <select data-testid="support-category" value={form.category} onChange={set("category")} className="bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none">
                    {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select data-testid="support-product" value={form.product} onChange={set("product")} className="bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none">
                    <option value="">Product (optional)</option>
                    {products.map((p) => <option key={p.product} value={p.product}>{p.product}</option>)}
                  </select>
                </div>
                <textarea data-testid="support-message" required rows={4} placeholder="Describe your issue..." value={form.message} onChange={set("message")} className="w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none resize-y" />
                <button data-testid="support-submit" disabled={submitting} className="w-full font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black py-3 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50">
                  {submitting ? "Sending..." : "Submit Ticket"}
                </button>
              </form>
            )}
          </div>

          {/* Live chat */}
          {buildMode ? (
            <AvaBuildChat onExit={() => setBuildMode(false)} />
          ) : (
          <div className="border border-zinc-800 bg-[#090A0C] flex flex-col h-[560px]">
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center gap-2">
              {isHuman ? <Headset size={20} weight="duotone" className="text-green-400" /> : <Robot size={20} weight="duotone" className="text-cyan-400" />}
              <h2 className="font-mono-p font-bold uppercase tracking-tight text-sm">{isHuman ? "Live Human Chat" : "Live AI Chat"}</h2>
              {agentName && <span className="text-xs text-zinc-500">· {agentName}</span>}
              <button onClick={() => setBuildMode(true)} data-testid="start-ava-build-btn"
                className="ml-auto flex items-center gap-1.5 font-mono-p text-[10px] uppercase tracking-widest border border-cyan-400/50 text-cyan-300 px-3 py-1.5 hover:bg-cyan-400/10 transition-colors">
                <Sparkle size={13} weight="fill" /> Build my AI iD Card
              </button>
              <span className={`w-2 h-2 rounded-full animate-pulse ${isHuman ? "bg-green-400" : "bg-cyan-400"}`} />
            </div>

            {/* product + name selectors (before first message) */}
            {!sessionId && (
              <div className="px-5 py-3 border-b border-zinc-800 grid grid-cols-2 gap-3">
                <select data-testid="chat-product" value={product} onChange={(e) => setProduct(e.target.value)} className="bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none">
                  {products.length === 0 && <option value="">General Support</option>}
                  {products.map((p) => <option key={p.product} value={p.product}>{p.product}</option>)}
                </select>
                <input data-testid="chat-name" value={visitor} onChange={(e) => setVisitor(e.target.value)} placeholder="Your name (optional)" className="bg-black border border-zinc-800 px-3 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" />
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.length === 0 && (
                <div className="flex gap-2">
                  <Robot size={20} className="text-cyan-400 shrink-0 mt-1" />
                  <div className="bg-black border border-zinc-800 px-4 py-2.5 text-sm text-zinc-300">
                    Hi! Pick a product and ask me anything — I'll help right away, or bring in a human if needed.
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                m.role === "system" ? (
                  <div key={i} data-testid={`chat-msg-${i}`} className="text-center">
                    <span className="inline-block text-[10px] uppercase tracking-widest font-mono-p text-zinc-500 bg-black border border-zinc-800 px-3 py-1">{m.text}</span>
                  </div>
                ) : (
                  <div key={i} data-testid={`chat-msg-${i}`} className={`flex gap-2 ${m.role === "customer" ? "flex-row-reverse" : ""}`}>
                    {m.role === "customer" ? <UserCircle size={20} className="text-zinc-500 shrink-0 mt-1" />
                      : m.role === "human" ? <Headset size={20} className="text-green-400 shrink-0 mt-1" />
                      : <Robot size={20} className="text-cyan-400 shrink-0 mt-1" />}
                    <div className={`max-w-[80%] px-4 py-2.5 text-sm ${m.role === "customer" ? "bg-cyan-400 text-black" : m.role === "human" ? "bg-black border border-green-500/40 text-zinc-200" : "bg-black border border-zinc-800 text-zinc-300"}`}>
                      {m.role !== "customer" && m.author && <p className="text-[10px] uppercase font-mono-p text-zinc-500 mb-1">{m.author}</p>}
                      <p className="whitespace-pre-wrap">{m.text}</p>
                    </div>
                  </div>
                )
              ))}
              {thinking && <div className="flex gap-2"><Robot size={20} className="text-cyan-400 mt-1" /><div className="bg-black border border-zinc-800 px-4 py-2.5 text-sm text-zinc-500 animate-pulse">Typing...</div></div>}
              <div ref={endRef} />
            </div>
            <div className="p-4 border-t border-zinc-800 flex items-center gap-2">
              <input data-testid="chat-widget-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder={isHuman ? "Message the team..." : "Ask the AI agent..."} className="flex-1 bg-black border border-zinc-800 px-4 py-2.5 text-sm focus:border-cyan-400/60 focus:outline-none" />
              <button data-testid="chat-widget-send" onClick={sendChat} disabled={thinking} className="w-10 h-10 bg-cyan-400 text-black flex items-center justify-center hover:bg-cyan-300 transition-colors disabled:opacity-50">
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  );
}
